import styled from '@emotion/styled';
import moment from 'moment';
import React from 'react';
import {
	Css,
	StyledHeaderTitle as HeaderTitle,
	type ITheme,
	Markdown,
} from '../../../ui';
import { getTextColor } from '../../../ui/components/Header/helpers';
import Carousel from '../../../ui/components/image-carousel/Carousel';
import { CarouselItem } from '../../../ui/components/image-carousel/CarouselItem';
import ImageCard from '../../../ui/components/image-carousel/ImageCard';
import Responsive from '../../../ui/utils/Responsive';
import { StyledMarkdownWrapper as BaseMarkdownWrapper } from '../../content-sections/ui/components/StyledMarkdownWrapper';
import {
	getOverlapWithHeaderStyles,
	PageHeader,
	SolidContentWrap,
	StyledHeader,
} from '../../shared/components';
import { PageLayout } from '../../shared/layout/page/PageLayout';
import { Color } from '../../shared/styles/Color';
import type { IDetailPageData } from '../domain/models/IDetailsPageData';
import ContentCategoryTag from './components/ContentCategoryTag';

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function DetailsPage({ page, post }: IDetailPageData) {
	const contentCategory = post.category?.fields.title;
	const formattedDate = moment(post.publicationDate).format('MM-DD-YYYY');

	return (
		<PageLayout
			showAlertsDialog
			page={page}
			metaData={{
				title: post.seoTitle || post.title,
				description: post.seoDescription,
			}}
		>
			<PageHeader page={page} hasCustomTitle>
				<StyledHeaderTitle>{post.title}</StyledHeaderTitle>
				{post.subtitle && (
					<StyledHeaderSubtitle>{post.subtitle}</StyledHeaderSubtitle>
				)}
			</PageHeader>
			{post.images && (
				<CarouselWrapper>
					<Carousel gap={18}>
						{post.images.map((image, index) => (
							<CarouselItem
								ariaSlideIndex={index}
								ariaSlideTitle={image.fields.title || ''}
								ariaTotalNumberOfSlides={post.images?.length || 0}
								key={image.id}
							>
								<ImageCard
									alt={image.fields.title}
									imageSrc={image.fields.file.url}
								/>
							</CarouselItem>
						))}
					</Carousel>
				</CarouselWrapper>
			)}
			<StyledContentWrap>
				<StyledFlexContainer>
					{contentCategory && (
						<ContentCategoryTag>{contentCategory}</ContentCategoryTag>
					)}
					<StyledDate dateTime={formattedDate}>{formattedDate}</StyledDate>
				</StyledFlexContainer>
				<StyledMarkdownWrapper>
					<Markdown source={post.body} />
				</StyledMarkdownWrapper>
			</StyledContentWrap>
		</PageLayout>
	);
}

const CarouselWrapper = styled.div<{}, ITheme>`
	${StyledHeader} + style + &,
      ${StyledHeader} + & {
		${getOverlapWithHeaderStyles()}
	}
`;

const StyledContentWrap = styled(SolidContentWrap)`
	margin-bottom: 126px;
	background: transparent;
	box-shadow: none;
`;

const StyledHeaderTitle = styled(HeaderTitle)`
	font-size: ${Css.pxToRem(52)};
	font-weight: 700;
	line-height: 62.4px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(32)};
		line-height: 38.4px;
	}
`;

const StyledHeaderSubtitle = styled.h2<{ inverted?: boolean }, ITheme>`
	color: ${getTextColor};

	font-size: ${Css.pxToRem(18)};
	font-weight: 700;
	line-height: 21.6px;

	margin-top: 24px;
	margin-bottom: 40px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
		line-height: 19.2px;
		margin-top: 16px;
		margin-bottom: 32px;
	}
`;

const StyledFlexContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
`;

const StyledDate = styled.time`
	font-size: ${Css.pxToRem(12)};
	font-weight: 500;
	line-height: 14.4px;
	letter-spacing: 1px;
	color: ${Color.TextGrey};
`;

const StyledMarkdownWrapper = styled(BaseMarkdownWrapper)`
	p,
	li {
		color: ${Color.TextGrey};
	}
	h2,
	h4,
	h5 {
		color: ${Color.TextGrey};
		font-weight: 800;
	}
`;
